import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import {
  ActivatedRouteSnapshot,
  CanActivate,
  Router,
  UrlTree,
} from '@angular/router';
import { Observable, from } from 'rxjs';
import { AuthFlowService } from '@core/services/auth-flow.service';
import { AuthService } from '@core/services/auth.service';

/**
 * Enforces the registration and onboarding sequence for authenticated users.
 */
@Injectable({
  providedIn: 'root',
})
export class RegistrationFlowGuard implements CanActivate {
  private router = inject(Router);
  private authService = inject(AuthService);
  private authFlowService = inject(AuthFlowService);
  private platformId = inject(PLATFORM_ID);

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    return from(this.evaluateRoute(route));
  }

  /**
   * Evaluates whether the user is allowed to load the requested registration route.
   */
  private async evaluateRoute(
    route: ActivatedRouteSnapshot,
  ): Promise<boolean | UrlTree> {
    const user = await this.authService.waitForHydratedUser(10000);
    if (!user) {
      return this.router.createUrlTree(['/login']);
    }

    // No email-verification wall (#943): an unverified account walks the
    // steps, creates its company and pays. firestore.rules no longer refuses
    // its company writes; only invites still need a verified address.

    const targetPath = route.routeConfig?.path;
    const allowedPath = await this.authFlowService.getNextRegistrationStep(user);
    const safeRedirectUrl = this.authFlowService.getSafeRedirectUrl();

    if (allowedPath === 'success' && targetPath !== 'success') {
      return this.router.parseUrl(safeRedirectUrl);
    }

    if (targetPath === 'success') {
      if (
        isPlatformBrowser(this.platformId) &&
        typeof sessionStorage !== 'undefined'
      ) {
        const allowSuccess =
          sessionStorage.getItem('registrationSuccessAllowed') === '1';
        if (allowSuccess) {
          sessionStorage.removeItem('registrationSuccessAllowed');
          return true;
        }
      }

      return this.router.parseUrl(safeRedirectUrl);
    }

    if (targetPath === allowedPath) {
      return true;
    }

    if (allowedPath === 'success') {
      return this.router.parseUrl(safeRedirectUrl);
    }

    return this.router.createUrlTree(['/register', allowedPath]);
  }
}
